import { useRef, useState } from 'react';

import { useGetResources } from '@hooks/useGetResources';
import { getUrlToReturnToAfterLogin, useLogin } from '@hooks/useLogin';
import { PageRoutes } from '@src/types/types';
import { useAppDispatch, useAppSelector } from '@store';
import { addLaeringsaktivitetThunk } from '@store/verktoy/thunks';
import { erVerktoyTilgjengelig } from '@utils/utils';
import { useLocation } from 'react-router';

import type { Verktoy } from '@src/types/minhelseentities';

import Button from '@helsenorge/designsystem-react/components/Button';
import Icon from '@helsenorge/designsystem-react/components/Icon';
import PlusLarge from '@helsenorge/designsystem-react/components/Icons/PlusLarge';
import Modal from '@helsenorge/designsystem-react/components/Modal';

import { trackTool } from '@helsenorge/framework-utils/adobe-analytics';
import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';

interface AddVerktoyElementProps {
  verktoy: Verktoy;
  erLaeringsaktivitet: boolean;
}

const AddVerktoyElement = ({ verktoy, erLaeringsaktivitet }: AddVerktoyElementProps): React.JSX.Element | null => {
  const [showModal, setShowModal] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isWaitingForAktivitetResponse = useAppSelector(globalState => globalState.verktoy.isWaitingForAktivitetResponse);
  const mineVerktoyListe = useAppSelector(globalState => globalState.verktoy.mineVerktoyListe);
  const resources = useGetResources();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const { login } = useLogin();
  const isLoggedIn = isAuthorized();

  const enteredDetailsPageFrom = location.state?.from || '';

  const kanLeggTiliMineVerktoy = (verktoy: Verktoy, isWaitingForAktivitetResponse: unknown): boolean => {
    const tilgjengelig = erVerktoyTilgjengelig(verktoy);
    const kreverForskrivning = verktoy.Laeringsressurs.KreverForskrivning === true;
    const fraOppgaveSide = enteredDetailsPageFrom === PageRoutes.oppgave;
    const aktivitetResponseIsNull = isWaitingForAktivitetResponse === null;
    const aktivitetResponseIsUndefined = isWaitingForAktivitetResponse === undefined;

    const result =
      tilgjengelig &&
      !kreverForskrivning &&
      !erLaeringsaktivitet &&
      !fraOppgaveSide &&
      !aktivitetResponseIsNull &&
      !aktivitetResponseIsUndefined;

    return result;
  };

  const addVerktoy = (): void => {
    if (isLoggedIn) {
      dispatch(addLaeringsaktivitetThunk({ verktoy }));
      trackTool('Verktøy', '', verktoy.Navn, 'legg til verktøy');
    } else {
      setShowModal(true);
    }
  };

  const urlToReturnToAfterLogin = getUrlToReturnToAfterLogin(location.pathname, verktoy.VerktoyId);
  if (!resources) {
    return null;
  }
  const erLagtTilIMineVerktoy: boolean = mineVerktoyListe?.some(item => item.VerktoyId === verktoy.VerktoyId) ?? false;
  if (kanLeggTiliMineVerktoy(verktoy, isWaitingForAktivitetResponse) && !erLagtTilIMineVerktoy) {
    return (
      <>
        <Button
          ref={buttonRef}
          variant="borderless"
          testId={`add-verktoy-knapp-${verktoy.Navn}`}
          onClick={(): void => {
            addVerktoy();
          }}
          disabled={isWaitingForAktivitetResponse}
        >
          {resources?.addVerktoyButtonText}
          <Icon svgIcon={PlusLarge} />
        </Button>
        {showModal && (
          <Modal
            title={resources.addVerktoyButtonText}
            description={resources.addVerktoy_notLoggedIn}
            primaryButtonText={resources.modalLoginButtonText}
            secondaryButtonText={resources.modalCancelButtonText}
            onSuccess={(): void => {
              setShowModal(false);
              login(buttonRef, urlToReturnToAfterLogin);
            }}
            onClose={(): void => setShowModal(false)}
          />
        )}
      </>
    );
  } else {
    return <></>;
  }
};

export default AddVerktoyElement;
